import React from "react";
import { Check, Clock, HandCoins, Handshake, Flag } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import SocialOutreachButtons from "@/components/SocialOutreachButtons";
import PendingSocialsBadge from "@/components/PendingSocialsBadge";
import { TopPerformerBadge } from "@/components/realtors/RealtorBadges";
import PhotoAvatar from "@/components/realtors/PhotoAvatar";
import QueueRankChip from "./QueueRankChip";
import HoldCountdownBadge from "./HoldCountdownBadge";
import NeedsFollowUpBadge from "./NeedsFollowUpBadge";
import { nameOf, formatCurrency, formatRelative } from "./utils";

export default function OutreachRow({
  row, selected, queueRank, held, holdExpiresAt, onSocialInteract,
  onToggle, onContactLogged,
  onFollowChanged, onLinkRemoved, onSocialVerified, onView, onPatch,
  onBrokerageClick,
  currency = "CAD", platformOrder,
}) {
  const name = nameOf(row);
  const lastTouchLabel = row.last_contact_at || row.latest_touch_at
    ? formatRelative(row.last_contact_at || row.latest_touch_at)
    : "Never";
  const refCount = Number(row.referral_count) || 0;
  const revenue = Number(row.referral_revenue_total) || 0;

  return (
    <div
      className={
        "grid grid-cols-[36px_1fr] gap-3 px-3 py-3 items-start hover:bg-slate-50 transition-colors" +
        (held
          ? " bg-sky-50 ring-1 ring-inset ring-sky-200"
          : queueRank ? " bg-amber-50/40" : "")
      }
      data-testid={`outreach-row-${row.id}`}
    >
      <div>
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          data-testid={`outreach-select-${row.id}`}
        />
      </div>
      <div className="min-w-0">
        <div className="flex items-start gap-2.5 min-w-0">
          <PhotoAvatar realtor={row} size="sm" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              {held && holdExpiresAt ? (
                <HoldCountdownBadge expiresAt={holdExpiresAt} id={row.id} />
              ) : (
                <QueueRankChip rank={queueRank} id={row.id} />
              )}
              {row.flagged_at ? (
                <Flag
                  className="h-3.5 w-3.5 text-rose-600 fill-rose-600 shrink-0"
                  aria-label="Flagged for follow-up"
                  data-testid={`flag-badge-${row.id}`}
                />
              ) : null}
              <button
                type="button"
                onClick={onView}
                className="text-sm font-semibold text-slate-900 hover:underline truncate text-left min-w-0"
                data-testid={`outreach-name-${row.id}`}
              >
                {name}
              </button>
              <TopPerformerBadge realtor={row} compact />
              <NeedsFollowUpBadge row={row} />
            </div>
            <div className="text-xs text-slate-500 truncate">
              {row.brokerage_id ? (
                <button
                  type="button"
                  onClick={() => onBrokerageClick?.(row.brokerage_id)}
                  className="text-left truncate hover:underline hover:text-slate-900 transition-colors"
                  data-testid={`outreach-brokerage-${row.id}`}
                >
                  {row.brokerage_name || "—"}
                </button>
              ) : (
                row.brokerage_name || "—"
              )}
            </div>
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              {row.met_in_person_at ? (
                <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 border-emerald-200 text-emerald-700">
                  <Check className="h-3 w-3" /> Met in person
                </Badge>
              ) : null}
              {refCount > 0 ? (
                <Badge variant="outline" className="text-[10px] gap-1 bg-rose-50 border-rose-200 text-rose-700">
                  <Handshake className="h-3 w-3" /> {refCount} referral{refCount === 1 ? "" : "s"}
                </Badge>
              ) : null}
              {revenue > 0 ? (
                <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 border-emerald-200 text-emerald-700">
                  <HandCoins className="h-3 w-3" /> {formatCurrency(revenue, currency)}
                </Badge>
              ) : null}
            </div>
          </div>
        </div>
        <div className="mt-1.5 space-y-1.5">
          <div className="flex flex-wrap items-center gap-1" onClickCapture={onSocialInteract}>
            <SocialOutreachButtons
              realtor={row}
              onContactLogged={onContactLogged}
              onFollowChanged={onFollowChanged}
              onLinkRemoved={onLinkRemoved}
              onSocialVerified={onSocialVerified}
              platformOrder={platformOrder}
            />
            <PendingSocialsBadge realtor={row} onChange={onPatch} onFollowChanged={onFollowChanged} />
          </div>
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {lastTouchLabel}
            {row.touch_count > 0 ? ` · ${row.touch_count} touches` : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
